// Stripe billing cadence — matches Stripe's `interval` values for subscriptions, plus one_time for top-ups.
export enum EBillingInterval {
  MONTH = "month",
  YEAR = "year",
  ONE_TIME = "one_time",
}

// Where in the UI the user clicked to start checkout. Drives funnel attribution.
export enum ECheckoutSource {
  COMPARISON_TABLE = "comparison_table",
  PRODUCT_PRICING_TABLE = "product_pricing_table",
  LEGACY_PRO_MONTHLY = "legacy_pro_monthly",
  TOPUP_MODAL = "topup_modal",
}

// Distinguishes subscription purchases from one-off top-ups on the PURCHASE event,
// so funnels can isolate first-time subscribers vs credit top-ups.
export enum EPurchaseType {
  SUBSCRIPTION = "subscription",
  TOPUP = "topup",
}

// Shared enums for properties. Names are unprefixed for backwards compatibility with
// existing importers — do not rename without a codemod.
export enum UserType {
  STUDENT = "student",
  PROFESSIONAL = "professional",
  ACADEMIC = "academic",
}

export enum ChatCompletionType {
  QUESTION = "question",
  SUMMARY = "summary",
  EXPLANATION = "explanation",
}

export enum SubscriptionTrigger {
  SPACE_LIMIT = "space_limit",
  FEATURE_UPSELL = "feature_upsell",
  MANUAL = "manual",
}

// ── Asset creation contract ──────────────────────────────────────────────────

/**
 * Product surface the creation request came from. This is the *product* level of the
 * Figma hierarchy (see flows.ts), deliberately coarser than `EAssetToolSource`, which
 * is tool level. One surface fans out to several tool sources.
 */
export enum EVMAssetSurface {
  GENERIC_CREATOR = "generic_creator",
  AD_STUDIO = "ad_studio",
  TALKING_AVATAR = "talking_avatar",
  BROLL = "broll",
  DIRECTOR_ENGINE = "director_engine",
  STATIC_ADS = "static_ads",
  // Not in the original Marketing Studio spec, but both ship today and produce assets.
  // Without members here their creations would have to lie about their surface.
  VIDEO_EDITOR = "video_editor",
  CAROUSEL_EDITOR = "carousel_editor",
}

/** Dominant media type the request asked for. Per-asset detail lives in `asset_types`. */
export enum EVMAssetOutputType {
  IMAGE = "image",
  VIDEO = "video",
  AUDIO = "audio",
  MIXED = "mixed",
}

/** How wide the request fans out. Drives the "is one terminal event expected" invariant. */
export enum EVMGenerationScope {
  /** Exactly one AssetJob. `creation_id` is that job's id. */
  SINGLE_ASSET = "single_asset",
  /** N sibling jobs of the same kind, grouped by `batchId`. */
  BATCH = "batch",
  /** A multi-stage graph of heterogeneous jobs, grouped by `sessionId`. */
  ORCHESTRATION = "orchestration",
}

/**
 * Is this a fresh request or a redo of a prior one? Deliberately NOT the place to
 * record "edit" — an edit is an operation (see {@link EVMTransformType}), and the two
 * are orthogonal: you can regenerate an upscale.
 */
export enum EVMGenerationAction {
  CREATE = "create",
  /** Carries `source_creation_id` pointing at the attempt being redone. */
  REGENERATE = "regenerate",
}

/**
 * What operation actually ran, coarsened from {@link EAssetJobType} to a size a
 * dashboard breakdown can read. The granular job types stay available on the
 * completion event via `asset_job_types`.
 *
 * Why this is a property and not a separate `ASSET_EDIT_*` event family: the lifecycle
 * is identical (one request → N jobs → one terminal event), so a second family would
 * mean a second copy of the creation_id reducer, the timeout sweeper and the orphan
 * detection — two places for the same bug — and every "did the user get an asset out"
 * funnel would become a permanent two-branch union query. Headline metrics isolate
 * de-novo work with `transform_type = GENERATE`, which is far cheaper to change later
 * than an event schema.
 */
export enum EVMTransformType {
  /** De novo synthesis. No input asset. */
  GENERATE = "generate",
  /** Semantic change to an existing asset: instruction edit, object removal, expand. */
  EDIT = "edit",
  /** Quality lift, content unchanged: upscale, enhance, relight. */
  ENHANCE = "enhance",
  /** Format or structure change: vectorize, resize, background removal, layer split. */
  CONVERT = "convert",
  /** Assembling existing assets into a new one: editor renders, workflows. */
  COMPOSE = "compose",
}

/** Did every requested asset land, or only some of them? */
export enum EVMCompletionStatus {
  COMPLETE = "complete",
  PARTIAL = "partial",
}

/**
 * Coarse failure bucket. Never carries the raw provider message — see the registry's
 * privacy class. `TIMEOUT` is what the sweeper emits for a request that never reached
 * a terminal state on its own.
 */
export enum EVMFailureCategory {
  PROVIDER_ERROR = "provider_error",
  TIMEOUT = "timeout",
  INSUFFICIENT_CREDITS = "insufficient_credits",
  VALIDATION = "validation",
  RATE_LIMITED = "rate_limited",
  CANCELLED = "cancelled",
  INTERNAL = "internal",
}

/**
 * Who asked for this. System-initiated work (auto-upscale on export, thumbnail
 * generation) must never enter a creation funnel — it is not a user request and
 * silently deflates every completion rate. Prefer not emitting at all for system
 * work; the operational AssetJob rows already cover it.
 */
export enum EVMInitiator {
  USER = "user",
  SYSTEM = "system",
}
